import { FlatLookupView } from "@/Common/Api/MedfarDictionaries/Dto/Responses/FlatLookupView";
import { TextboxControl } from "@/Common/Components/Inputs/TextboxControl";
import { TextboxMaskedControl } from "@/Common/Components/Inputs/TextboxMaskedControl";
import { SearchAddress } from "@/Common/Components/Search/SearchAddress";
import { ArrayHelper } from "@/Common/Helpers/ArrayHelper";
import { ValidationHelper } from "@/Common/Helpers/ValidationHelper";
import { CountryNames } from "@/Common/Resources/Lookups/Enums/CommonLookupCodes";
import { CountrySelect } from "./CountrySelect";

class AddressControlDTO {
    public selectedCountryLookupId: string;
    public selectedRegionLookupId: string;
    public civicAddressEdit: string;
    public cityEdit: string;
    public postalCodeEdit: string;
    public isPostalCodeValid: boolean = true;
}

class AddressControlState extends AddressControlDTO {
    public availableCountryLookupIds: FlatLookupView[];
    public availableRegionLookupIds: FlatLookupView[];

    public constructor(init?: Partial<AddressControlState>) {
        super();
        Object.assign(this, init);
    }
}

interface IAddressControlProps {
    id: string;
    dataCy?: string;

    onNotifyParentCallback: (dto: AddressControlDTO) => void

    // React doesn't support NULL value on input element, please pass either string or empty string
    initialStreetAddress: string;
    initialCity: string;
    initialPostalCode: string;
    initialCountryLookupId: string;
    initialStateLookupId: string;

    rowStyle?: React.CSSProperties;
    labelStyle?: React.CSSProperties;
    bodyStyle?: React.CSSProperties;

    rowClassName?: string;
    labelClassName?: string;
    bodyClassName?: string;

    needValidation?: boolean;
    alwaysShowCityAndZipCode?: boolean;

    renderAsTableBody?: boolean;
    isDisabled?: boolean;
}

class AddressControl extends React.Component<IAddressControlProps, AddressControlState> {
    private validationHelper = new ValidationHelper();

    public constructor(props: IAddressControlProps) {
        super(props);
        this.state = new AddressControlState({
            availableCountryLookupIds: cacheStore.GetCountryLookups(),
            availableRegionLookupIds: cacheStore.GetCountryRegionLookups(cacheStore.GetCountryLookups().filter(c => c.LookupID === this.props.initialCountryLookupId)[0]),
            civicAddressEdit: props.initialStreetAddress,
            postalCodeEdit: props.initialPostalCode,
            cityEdit: props.initialCity,
            selectedCountryLookupId: props.initialCountryLookupId,
            selectedRegionLookupId: props.initialStateLookupId
        });
    }

    public render(): JSX.Element {
        if (this.props.renderAsTableBody)
            return this.renderAddressControlAsTableBody();

        return this.renderAddressControl();
    }

    private renderAddressControlAsTableBody(): JSX.Element {
        const rowClassName = this.props.rowClassName;
        const labelClassName = this.props.labelClassName;
        const bodyClassName = this.props.bodyClassName;
        const showCityAndPostalCode = !IsNullOrWhiteSpace(this.state.civicAddressEdit) || this.props.alwaysShowCityAndZipCode;

        return (
            <tbody id={"adressControl_" + this.props.id}>
                <tr className={rowClassName} style={this.props.rowStyle}>
                    <td className={labelClassName ?? "c1"} style={this.props.labelStyle}>
                        {this.renderCountryLabel()}
                    </td>
                    <td className={bodyClassName ?? "c2"} style={this.props.bodyStyle}>
                        {this.renderCountrySelector()}
                    </td>
                </tr>
                <tr className={rowClassName} style={this.props.rowStyle}>
                    <td className={labelClassName ?? "c1"} style={this.props.labelStyle}>
                        {this.renderStateLabel()}
                    </td>
                    <td className={bodyClassName ?? "c2"} style={this.props.bodyStyle}>
                        {this.renderStateDropdown()}
                    </td>
                </tr>
                <tr className={rowClassName} style={this.props.rowStyle}>
                    <td className={labelClassName ?? "c1"} style={this.props.labelStyle}>
                        {this.renderStreetAddressLabel()}
                    </td>
                    <td className={bodyClassName ?? "c2"} style={this.props.bodyStyle}>
                        {this.renderStreetAddressSearchBox()}
                    </td>
                </tr>
                {showCityAndPostalCode && (
                    <tr className={rowClassName} style={this.props.rowStyle}>
                        <td className={labelClassName ?? "c1"} style={this.props.labelStyle}>
                            {this.renderCityLabel()}
                        </td>
                        <td className={bodyClassName ?? "c2"} style={this.props.bodyStyle}>
                            {this.renderCityTextbox()}
                        </td>
                    </tr>
                )}
                {showCityAndPostalCode && (
                    <tr className={rowClassName} style={this.props.rowStyle}>
                        <td className={labelClassName ?? "c1"} style={this.props.labelStyle}>
                            {this.renderPostalCodeLabel()}
                        </td>
                        <td className={bodyClassName ?? "c2"} style={this.props.bodyStyle}>
                            {this.renderPostalCodeTextbox()}
                        </td>
                    </tr>
                )}
            </tbody>
        );
    }

    private renderAddressControl(): JSX.Element {
        const rowClassName = this.props.rowClassName ?? "flex-container";
        const labelClassName = this.props.labelClassName ?? "";
        const bodyClassName = this.props.bodyClassName ?? "";

        return (
            <div id={"adressControl_" + this.props.id}>
                <div className={rowClassName} style={this.props.rowStyle}>
                    <div className={labelClassName} style={this.props.labelStyle}>
                        {this.renderCountryLabel()}
                    </div>
                    <div className={bodyClassName} style={this.props.bodyStyle}>
                        {this.renderCountrySelector()}
                    </div>
                </div>
                <div className={rowClassName} style={this.props.rowStyle}>
                    <div className={labelClassName} style={this.props.labelStyle}>
                        {this.renderStateLabel()}
                    </div>
                    <div className={bodyClassName} style={this.props.bodyStyle}>
                        {this.renderStateDropdown()}
                    </div>
                </div>
                <div className={rowClassName} style={this.props.rowStyle}>
                    <div className={labelClassName} style={this.props.labelStyle}>
                        {this.renderStreetAddressLabel()}
                    </div>
                    <div className={bodyClassName} style={this.props.bodyStyle}>
                        {this.renderStreetAddressSearchBox()}
                    </div>
                </div>
                {(!IsNullOrWhiteSpace(this.state.civicAddressEdit) || this.props.alwaysShowCityAndZipCode) && (
                    <div>
                        <div className={rowClassName} style={this.props.rowStyle}>
                            <div className={labelClassName} style={this.props.labelStyle}>
                                {this.renderCityLabel()}
                            </div>
                            <div className={bodyClassName} style={this.props.bodyStyle}>
                                {this.renderCityTextbox()}
                            </div>
                        </div>

                        <div className={rowClassName} style={this.props.rowStyle}>
                            <div className={labelClassName} style={this.props.labelStyle}>
                                {this.renderPostalCodeLabel()}
                            </div>
                            <div className={bodyClassName} style={this.props.bodyStyle}>
                                {this.renderPostalCodeTextbox()}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    private renderCountryLabel(): JSX.Element {
        return this.renderLabel("CountryLabel");
    }

    private renderStateLabel(): JSX.Element {
        return this.renderLabel("StateProvinceLabel");
    }

    private renderStateDropdown(): JSX.Element {
        return (
            <select
                data-cy={this.props.dataCy ? `${this.props.dataCy}-region` : null}
                className="form-control myle-control"
                value={this.state.selectedRegionLookupId}
                onChange={(e: any) => this.onRegionChange(e.target.value)}
                disabled={this.props.isDisabled}
            >
                {this.state.availableRegionLookupIds.map((z: FlatLookupView) =>
                    <option key={z.LookupID} value={z.LookupID}>{cacheStore.GetLocalizedValueByLookupId(z.LookupID)}</option>
                )}
            </select>
        );
    }

    private renderCityLabel(): JSX.Element {
        return this.renderLabel("CityLabel");
    }

    private renderCityTextbox(): JSX.Element {
        return (
            <TextboxControl
                dataCy={this.props.dataCy ? `${this.props.dataCy}-city` : null}
                valid={!this.props.needValidation || !IsNullOrWhiteSpace(this.state.cityEdit)}
                debounceMs={200}
                className="form-control"
                onChange={city => this.mutateState(s => s.cityEdit = city)}
                value={this.state.cityEdit}
                disabled={this.props.isDisabled}
            />
        );
    }

    private renderPostalCodeLabel(): JSX.Element {
        return this.renderLabel("ZipPostalCodeLabel");
    }

    private renderPostalCodeTextbox(): JSX.Element {
        const zipvalidationRule = this.validationHelper.getPostalCodeValidationRule(this.state.selectedCountryLookupId, null); // no need for region when checking for postal code format?
        const zipmask: string = zipvalidationRule?.EditMaskPattern ?? "";
        const zipvalidationErrorMessage: string = zipvalidationRule?.ValidationErrorMessageLookupId ? cacheStore.GetLocalizedValueByLookupId(zipvalidationRule.ValidationErrorMessageLookupId) : "";

        return (
            <TextboxMaskedControl
                dataCy={this.props.dataCy ? `${this.props.dataCy}-postalCode` : null}
                isValid={this.validatePostalCode(this.state.postalCodeEdit)}
                id={"zipcode" + this.props.id}
                onChangeOperation={null}
                value={!IsNullOrWhiteSpace(this.state.postalCodeEdit) ? this.state.postalCodeEdit : ""}
                allowEmpty={!this.props.needValidation}
                onChangeFunction={(e: any) => this.onPostalCodeChange(e)}
                mask={zipmask}
                validationErrorMessage={zipvalidationErrorMessage}
                disabled={this.props.isDisabled}
            />
        );
    }

    private renderStreetAddressSearchBox(): JSX.Element {
        return (
            <SearchAddress
                dataCy={this.props.dataCy ? `${this.props.dataCy}-street` : null}
                onItemSelectCallback={({ streetAddress, city, postalCode, stateCode, countryCode }) => this.onAddressSelect(streetAddress, city, postalCode, stateCode, countryCode)}
                streetAddress={this.props.initialStreetAddress}
                onTextChangeCallback={address => this.mutateState(s => s.civicAddressEdit = address)}
                debounce={300}
                countryCode={cacheStore.GetCountryIsoCodeByLookUpId(this.state.selectedCountryLookupId)}
                needValidation={this.props.needValidation}
                isDisabled={this.props.isDisabled}
            />
        );
    }

    private renderStreetAddressLabel(): JSX.Element {
        return this.renderLabel("StreetAddressLabel");
    }

    private renderLabel(labelName: string): JSX.Element {
        return (
            <label className="form-label">
                {cacheStore.GetString(labelName)}
            </label>
        );
    }

    private mutateState(mutation: (s: AddressControlState) => void, then: () => void = null): void {
        let s = new AddressControlState();
        mutation(s);
        this.setState(s, () => {
            this.props.onNotifyParentCallback(this.state);

            if (then)
                then();
        });
    }

    private validatePostalCode(val: string): boolean {
        if (this.props.needValidation && IsNullOrWhiteSpace(val)) {
            return false;
        }

        // fetch postalcode validation info
        var validationRule = this.validationHelper.getPostalCodeValidationRule(this.state.selectedCountryLookupId, null); // no need for region when checking for postal code format?
        var validationRegex: string = validationRule ? validationRule.ValidationRegexPattern : "";

        var isValid = true;

        // check if valid with regex
        if (validationRegex && validationRegex.length > 0) {
            var regexp = new RegExp(validationRegex, "i");
            isValid = ValidationHelper.isExactMatch(val, regexp);
        }

        return isValid;
    }

    private onCountryChange(value: string): void {
        this.mutateState(s => {
            s.selectedCountryLookupId = value;
            s.availableRegionLookupIds = cacheStore.GetCountryRegionLookups(cacheStore.GetLookupById(value));
            s.selectedRegionLookupId = cacheStore.GetCountryRegionLookups(cacheStore.GetLookupById(value))[0].LookupID;
        });
    }

    private onRegionChange(value: string): void {
        this.mutateState(s => {
            s.selectedRegionLookupId = value;
        });
    }

    private onPostalCodeChange(code: string): void {
        this.mutateState(s => {
            s.postalCodeEdit = code;
            s.isPostalCodeValid = this.validatePostalCode(code);
        });
    }

    private onAddressSelect(street: string, city: string, zipCode: string, stateCode: string, countryCode: string): void {
        const lookUpCode = cacheStore.GetCountryLookupCodeByISOCodeAlpha2(countryCode);
        const countryLookupId = cacheStore.FindLookupByLookupCode(lookUpCode, CountryNames.Code)?.LookupID;
        this.mutateState(s => {
            s.civicAddressEdit = street;
            s.cityEdit = city;
            s.postalCodeEdit = zipCode;
            s.isPostalCodeValid = this.validatePostalCode(zipCode);
            s.selectedRegionLookupId = this.toStateLookupId(stateCode, countryLookupId);
            s.selectedCountryLookupId = countryLookupId;
        });
    }

    private toStateLookupId(stateCode: string, countryLookupId: string = null): string {
        let countryLookup: FlatLookupView = ArrayHelper.Find(cacheStore.GetCountryLookups(), x => x.LookupID == countryLookupId);
        let countryName = "Canada";

        if (countryLookup != null)
            countryName = countryLookup.en;

        return stateCode ? cacheStore.FindLookupByLookupCode(countryName + "State_" + stateCode)?.LookupID : "";
    }

    private renderCountrySelector(): JSX.Element {
        return (
            <CountrySelect
                dataCy={this.props.dataCy ? `${this.props.dataCy}-country` : null}
                isDisabled={this.props.isDisabled}
                onCountryChange={value => this.onCountryChange(value)}
                initialCountryLookupId={this.state.selectedCountryLookupId}
            />
        );
    }
}

export { AddressControl, AddressControlDTO };
